import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MessageCircle, Sunrise, PenLine, Brain, Wand2, BellRing,
  Paperclip, CalendarCheck, Users, Plug, ArrowRight,
} from "lucide-react";
import { MarketingNav, MarketingFooter } from "@/components/MarketingShell";
import { useSite, renderHl } from "@/lib/site";
import "./demo.css";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] },
};

const STORY = [
  { icon: MessageCircle, tag: "WhatsApp Agent",
    miss: "Your inbox stays at the office. Your day doesn't.",
    gain: "Read, triage, draft and send email from a WhatsApp chat — by text or voice note. The app you already check 60 times a day becomes your email command line." },
  { icon: Sunrise, tag: "Morning Brief",
    miss: "You wake up to 40 unread and scan every single one.",
    gain: "One WhatsApp message before your day starts: VIP mail first, who still owes you a reply, follow-ups due, today's meetings — and a short digest of the rest." },
  { icon: PenLine, tag: "Your Writing Style",
    miss: "AI drafts that sound like a polite robot wrote them.",
    gain: "The agent learns from your sent mail — your greeting, your sign-off, your level of formality — so every draft sounds like you on a good day." },
  { icon: Brain, tag: "Agent Memory",
    miss: "You re-read old threads to remember what was agreed.",
    gain: "\"Laura prefers Thursdays.\" \"Acme pays at 45 days.\" Durable facts about every person, organization and project — used automatically in every reply." },
  { icon: Wand2, tag: "Plain-Language Rules",
    miss: "You archive the same newsletters every morning, by hand.",
    gain: "\"Archive every newsletter.\" \"Alert me on WhatsApp when a VIP writes.\" Say it once in the chat — it runs forever." },
  { icon: BellRing, tag: "Follow-ups",
    miss: "Deals go quiet because nobody chased the reply.",
    gain: "\"Follow up if she doesn't reply by Friday.\" The agent watches the thread and nudges at the right moment. No more mental lists." },
  { icon: Paperclip, tag: "Attachment Reading",
    miss: "20-page contracts marked \"read later\". Later never comes.",
    gain: "The agent reads PDFs and documents inside your email. Ask \"what's the penalty clause?\" and get the answer, not the homework." },
  { icon: CalendarCheck, tag: "Calendar",
    miss: "Four emails back and forth just to pick a meeting time.",
    gain: "\"Schedule a meeting with Elena tomorrow at 2pm.\" Straight into Google Calendar — invite sent, thread linked, reminder set." },
  { icon: Users, tag: "Teams",
    miss: "Every teammate drowning in their own inbox, separately.",
    gain: "Buy seats like Google Workspace. Every member gets their own agent — memory, rules and briefs — under one billing and one admin." },
  { icon: Plug, tag: "API & MCP",
    miss: "Your tools can't see your inbox.",
    gain: "Scoped API keys, signed webhooks and an MCP server — connect Claude, ChatGPT, Cursor or your own software to your mail." },
];

export default function FeaturesPage() {
  const { g, brand, seo } = useSite("features");
  useEffect(() => { document.title = seo?.meta_title || "Features — Agentic Inbox"; window.scrollTo(0, 0); }, [seo]);

  return (
    <div className="demo-root min-h-screen" data-testid="features-page">
      <MarketingNav active="/features" />

      {/* HERO */}
      <section className="relative demo-grain pt-40 pb-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div {...fadeUp}>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#86868B] mb-4">{g("hero_label", "Product")}</p>
            <h1 className="text-5xl md:text-7xl font-semibold tracking-tighter leading-[1.05] text-balance">
              {g("hero_title", "Everything email should have learned to do by now.")}
            </h1>
            <p className="mt-6 text-lg md:text-xl text-[#86868B] leading-relaxed max-w-2xl mx-auto">
              {g("hero_sub", "Ten things you do by hand today — and what your week looks like when an agent does them instead.")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* FOCUS FLOW — sticky statement left, story cards right */}
      <section className="px-6 pb-28">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="md:sticky md:top-28">
              <motion.div {...fadeUp}>
                <h2 className="text-4xl md:text-5xl font-medium tracking-tight leading-[1.1]">
                  {renderHl(g("side_title", "What you're **missing.**"))}
                </h2>
                <p className="mt-5 text-[15px] text-[#86868B] leading-relaxed max-w-xs">
                  {g("side_sub", "Each card is something you still do by hand — and how it disappears the day you connect your inbox.")}
                </p>
                <Link to="/pricing" data-testid="features-side-cta"
                  className="demo-pill inline-flex items-center gap-2 mt-8 bg-[#0071E3] hover:bg-[#0077ED] text-white text-[14px] font-semibold px-6 py-3">
                  {g("side_cta", "See plans")} <ArrowRight size={14} />
                </Link>
              </motion.div>
            </div>
          </div>
          <div className="md:col-span-8 space-y-6">
            {STORY.map((s, i) => (
              <motion.div key={s.tag} {...fadeUp} transition={{ duration: 0.5, delay: 0.04 }}
                className="demo-card p-8 md:p-10" data-testid={`story-card-${i}`}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-11 h-11 rounded-2xl bg-[#0071E3]/10 text-[#0071E3] flex items-center justify-center">
                    <s.icon size={21} strokeWidth={1.8} />
                  </div>
                  <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-[#86868B]">{g(`story_${i + 1}_tag`, s.tag)}</p>
                </div>
                <p className="text-[13px] font-semibold uppercase tracking-widest text-[#86868B] mb-1.5">{g("label_without", "Without it")}</p>
                <p className="demo-heading text-xl md:text-2xl font-medium tracking-tight text-[#1d1d1f]/50 line-through decoration-[#1d1d1f]/20 decoration-2">
                  {g(`story_${i + 1}_miss`, s.miss)}
                </p>
                <p className="text-[13px] font-semibold uppercase tracking-widest text-[#0071E3] mt-6 mb-1.5">{g("label_with", "With")} {brand.app_name || "Agentic Inbox"}</p>
                <p className="text-[16px] md:text-[17px] text-[#1d1d1f]/85 leading-relaxed">{g(`story_${i + 1}_gain`, s.gain)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-28 px-6 bg-[#1d1d1f] demo-grain relative">
        <div className="max-w-3xl mx-auto text-center relative">
          <motion.div {...fadeUp}>
            <h2 className="text-4xl md:text-6xl font-semibold tracking-tighter text-[#F5F5F7] text-balance">
              {g("cta_title", "Stop doing inbox chores. Start reviewing finished work.")}
            </h2>
            <p className="mt-5 text-lg text-[#86868B]">{g("cta_sub", "Connect Gmail, link WhatsApp, done in 3 minutes.")}</p>
            <Link to="/pricing" data-testid="features-footer-cta"
              className="demo-pill inline-block mt-9 bg-white text-[#1d1d1f] text-[15px] font-semibold px-9 py-4 hover:bg-[#F5F5F7]">
              {g("cta_btn", "See plans & pricing")}
            </Link>
          </motion.div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
